/**
 * Quiz / assessment domain types, runtime guards, and fetch helpers.
 *
 * Module: lib/quizzes
 * Layer:  util / data-access
 *
 * Purpose:
 *     Defines the student-facing shapes returned by the `/api/quizzes/*`
 *     endpoints (quiz catalog summaries, a single quiz with its questions but
 *     no answer keys, and a graded attempt result), validates every response at
 *     the network boundary with hand-written runtime guards (the project does
 *     not declare zod), and exposes credentialed fetch helpers used by the
 *     /quizzes pages. Answer keys never reach the browser before submission —
 *     the question shape carries only display options, and correctness/feedback
 *     arrives only in the attempt result.
 *
 * AI context:
 *     - Entry points called by: src/app/quizzes/** pages and the colocated
 *       components under src/components/quizzes.
 *     - Depends on: the browser `fetch` API only (matches lib/daily).
 *     - Side effects: network I/O via the fetch helpers.
 *     - Invariants: a resolved value always passed its runtime guard; on any
 *       non-OK response or malformed body the read helpers return `null` so
 *       callers can render an error/empty state instead of crashing. The submit
 *       helper returns `null` on failure rather than throwing.
 *     - Replacement cost: LOW.
 *
 * Threat model:
 *     - Responses are untrusted JSON. The `is*` guards are the trust boundary:
 *       only objects with the exact expected primitive shape survive, so the UI
 *       never renders attacker-controlled object keys. The catalog/question
 *       payloads deliberately omit answer keys; the only correctness data is in
 *       the post-submission feedback list.
 */

/** The four question types the student UI can render. */
export type QuestionType = 'multiple_choice' | 'true_false' | 'short_answer' | 'fill_blank';

/** One selectable option within a multiple-choice question. */
export interface QuizOption {
  readonly id: number;
  readonly text: string;
}

/** A single question as shown to a student (no answer key). */
export interface QuizQuestion {
  readonly id: number;
  readonly question_text: string;
  readonly question_type: QuestionType;
  readonly options: readonly QuizOption[];
  readonly points: number;
  readonly order: number;
}

/** Catalog summary card shape for the quiz listing. */
export interface QuizSummary {
  readonly id: number;
  readonly title: string;
  readonly description: string | null;
  readonly question_count: number;
  readonly time_limit_minutes: number | null;
  readonly passing_score: number;
  readonly course_id: number | null;
  readonly course_title: string | null;
}

/** A full quiz with its ordered questions, used by the taking page. */
export interface QuizDetail {
  readonly id: number;
  readonly title: string;
  readonly description: string | null;
  readonly time_limit_minutes: number | null;
  readonly passing_score: number;
  readonly course_id: number | null;
  readonly course_title: string | null;
  readonly questions: readonly QuizQuestion[];
}

/** Per-question grading feedback returned after submission. */
export interface QuestionFeedback {
  readonly question_id: number;
  readonly correct: boolean;
  readonly correct_answer: string;
  readonly explanation: string | null;
}

/** The graded outcome of one quiz attempt. */
export interface QuizAttemptResult {
  readonly id: number;
  readonly score: number;
  readonly passed: boolean;
  readonly correct_count: number;
  readonly total_questions: number;
  readonly time_taken_seconds: number;
  readonly feedback: readonly QuestionFeedback[];
}

/** A single answer the student submits, keyed by question id. */
export interface QuizAnswer {
  readonly question_id: number;
  readonly answer: string;
}

/** Optional filters for the catalog endpoint. */
export interface QuizListParams {
  readonly courseId?: number;
  readonly search?: string;
}

/** The primitive types a guarded field may hold. */
type FieldType = 'string' | 'number' | 'boolean';

/** Narrow an unknown value to a non-null record. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Verify every entry of a field->type table against a record (data-driven).
 *
 * @param row - The candidate record.
 * @param fields - Field-name -> expected primitive type table.
 * @returns `true` when every listed field has its expected primitive type.
 */
function fieldsMatch(
  row: Record<string, unknown>,
  fields: Readonly<Record<string, FieldType>>,
): boolean {
  return Object.entries(fields).every(([name, expected]) => typeof row[name] === expected);
}

/**
 * True when `value` is `null` or matches `expected` primitive type.
 *
 * @param value - The candidate field value.
 * @param expected - The non-null primitive type to accept.
 * @returns `true` for `null` or a matching primitive.
 */
function isNullOr(value: unknown, expected: FieldType): boolean {
  return value === null || typeof value === expected;
}

/** The set of valid question-type discriminants (membership table). */
const QUESTION_TYPES: ReadonlySet<QuestionType> = new Set<QuestionType>([
  'multiple_choice',
  'true_false',
  'short_answer',
  'fill_blank',
]);

/** Field schema for {@link QuizOption}. */
const OPTION_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  text: 'string',
};

/** Required primitive fields for {@link QuizQuestion} (type/options checked separately). */
const QUESTION_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  question_text: 'string',
  points: 'number',
  order: 'number',
};

/** Required primitive fields for {@link QuizSummary} (nullable fields checked separately). */
const SUMMARY_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  title: 'string',
  question_count: 'number',
  passing_score: 'number',
};

/** Required primitive fields for {@link QuizDetail} (nullable/array fields checked separately). */
const DETAIL_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  title: 'string',
  passing_score: 'number',
};

/** Required primitive fields for {@link QuestionFeedback}. */
const FEEDBACK_FIELDS: Readonly<Record<string, FieldType>> = {
  question_id: 'number',
  correct: 'boolean',
  correct_answer: 'string',
};

/** Required primitive fields for {@link QuizAttemptResult}. */
const RESULT_FIELDS: Readonly<Record<string, FieldType>> = {
  id: 'number',
  score: 'number',
  passed: 'boolean',
  correct_count: 'number',
  total_questions: 'number',
  time_taken_seconds: 'number',
};

/** Type guard for {@link QuizOption}. */
function isQuizOption(value: unknown): value is QuizOption {
  const row = asRecord(value);
  return row !== null && fieldsMatch(row, OPTION_FIELDS);
}

/** Type guard for the {@link QuestionType} discriminant. */
function isQuestionType(value: unknown): value is QuestionType {
  return typeof value === 'string' && QUESTION_TYPES.has(value as QuestionType);
}

/** Type guard for {@link QuizQuestion}. */
export function isQuizQuestion(value: unknown): value is QuizQuestion {
  const row = asRecord(value);
  if (row === null) {
    return false;
  }
  const options = row['options'];
  const optionsOk = Array.isArray(options) && options.every(isQuizOption);
  return optionsOk && isQuestionType(row['question_type']) && fieldsMatch(row, QUESTION_FIELDS);
}

/** True when a record's shared nullable quiz metadata fields are well-typed. */
function quizNullablesOk(row: Record<string, unknown>): boolean {
  return (
    isNullOr(row['description'], 'string') &&
    isNullOr(row['time_limit_minutes'], 'number') &&
    isNullOr(row['course_id'], 'number') &&
    isNullOr(row['course_title'], 'string')
  );
}

/** Type guard for {@link QuizSummary}. */
export function isQuizSummary(value: unknown): value is QuizSummary {
  const row = asRecord(value);
  if (row === null || !quizNullablesOk(row)) {
    return false;
  }
  return fieldsMatch(row, SUMMARY_FIELDS);
}

/** Type guard for an array of {@link QuizSummary}. */
export function isQuizSummaryList(value: unknown): value is readonly QuizSummary[] {
  return Array.isArray(value) && value.every(isQuizSummary);
}

/** Type guard for {@link QuizDetail}. */
export function isQuizDetail(value: unknown): value is QuizDetail {
  const row = asRecord(value);
  if (row === null || !quizNullablesOk(row) || !fieldsMatch(row, DETAIL_FIELDS)) {
    return false;
  }
  const questions = row['questions'];
  return Array.isArray(questions) && questions.every(isQuizQuestion);
}

/** Type guard for {@link QuestionFeedback}. */
function isQuestionFeedback(value: unknown): value is QuestionFeedback {
  const row = asRecord(value);
  if (row === null) {
    return false;
  }
  return isNullOr(row['explanation'], 'string') && fieldsMatch(row, FEEDBACK_FIELDS);
}

/** Type guard for {@link QuizAttemptResult}. */
export function isQuizAttemptResult(value: unknown): value is QuizAttemptResult {
  const row = asRecord(value);
  if (row === null || !fieldsMatch(row, RESULT_FIELDS)) {
    return false;
  }
  const feedback = row['feedback'];
  return Array.isArray(feedback) && feedback.every(isQuestionFeedback);
}

/** Build a credentialed GET init, optionally cancellable. */
function getInit(signal?: AbortSignal): RequestInit {
  return signal ? { credentials: 'include', signal } : { credentials: 'include' };
}

/**
 * Fetch and validate one quiz resource.
 *
 * @typeParam T - The validated payload type.
 * @param path - Absolute `/api/quizzes/*` path.
 * @param guard - Runtime guard for the decoded body.
 * @param signal - Optional abort signal so callers can cancel in-flight loads.
 * @returns The validated payload, or `null` on any non-OK or malformed response.
 * @remarks Sends the opaque session cookie via `credentials: 'include'`; never
 *   throws on HTTP/parse errors, so callers can render a graceful error state.
 */
async function fetchQuizResource<T>(
  path: string,
  guard: (value: unknown) => value is T,
  signal?: AbortSignal,
): Promise<T | null> {
  const res = await fetch(path, getInit(signal));
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return guard(body) ? body : null;
}

/** Stringify the course id filter, or empty when absent. */
function courseIdParam(params?: QuizListParams): string {
  return params?.courseId === undefined ? '' : String(params.courseId);
}

/** Build the catalog query string from optional filters. */
function buildListQuery(params?: QuizListParams): string {
  const search = new URLSearchParams();
  // Data-driven: (param-name -> raw value) pairs, skipping any empty value.
  const entries: ReadonlyArray<readonly [string, string]> = [
    ['course_id', courseIdParam(params)],
    ['search', params?.search ?? ''],
  ];
  const present = entries.filter(([, raw]) => raw !== '');
  for (const [name, raw] of present) {
    search.set(name, raw);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Fetch the quiz catalog, optionally filtered by course or search term.
 *
 * @param params - Optional course id / search filters.
 * @param signal - Optional abort signal.
 * @returns The validated quiz summaries, or `null` on failure.
 */
export function fetchQuizzes(
  params?: QuizListParams,
  signal?: AbortSignal,
): Promise<readonly QuizSummary[] | null> {
  const path = `/api/quizzes${buildListQuery(params)}`;
  return fetchQuizResource(path, isQuizSummaryList, signal);
}

/**
 * Fetch a single quiz with its student-view questions.
 *
 * @param id - The quiz id.
 * @param signal - Optional abort signal.
 * @returns The validated {@link QuizDetail}, or `null` on failure.
 */
export function fetchQuiz(id: number, signal?: AbortSignal): Promise<QuizDetail | null> {
  return fetchQuizResource(`/api/quizzes/${String(id)}`, isQuizDetail, signal);
}

/**
 * Submit a completed attempt and return its graded result.
 *
 * @param quizId - The quiz being submitted.
 * @param answers - One answer per question the student attempted.
 * @param signal - Optional abort signal.
 * @returns The validated {@link QuizAttemptResult}, or `null` on failure.
 * @remarks POSTs the answer list; the backend grades and returns feedback. Never
 *   throws on HTTP/parse errors so callers can show a submission error state.
 */
export async function submitQuizAttempt(
  quizId: number,
  answers: readonly QuizAnswer[],
  signal?: AbortSignal,
): Promise<QuizAttemptResult | null> {
  const init: RequestInit = {
    ...getInit(signal),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  };
  const res = await fetch(`/api/quizzes/${String(quizId)}/attempts`, init);
  if (!res.ok) {
    return null;
  }
  const body: unknown = await res.json();
  return isQuizAttemptResult(body) ? body : null;
}
